import { useToast } from '@/components/feedback/toast';
import { ApiError } from '@/lib/api/errors';

import { REPLACEMENT_APPROVED_MESSAGE } from '../constants';
import { listReplacementFieldInstallers } from '../replacements/api';
import { useApproveReplacement } from '../replacements/hooks';

import { ApproveReplacementModal } from './approve-replacement-modal';
import { AssignInstallerModal } from './assign-installer-modal';
import { RejectReplacementModal } from './reject-replacement-modal';

/** Which action overlay is open (from the row ellipsis or the detail drawer) and for which row.
 *  Minimal row shape so both a `ReplacementRow` and a `ReplacementDetail` satisfy it. */
export type ReplacementOverlay = {
  kind: 'reject' | 'approve-choice' | 'approve-assign';
  row: { id: string; orderNumber: string };
} | null;

function AssignArm({
  id,
  onBack,
  onApproved,
}: {
  id: string;
  onBack: () => void;
  onApproved: () => void;
}) {
  const approve = useApproveReplacement(id);
  const toast = useToast();

  return (
    <AssignInstallerModal
      open
      // AC: the assign step never dismisses on an outside click; Cancel returns to the choice
      // popup discarding entries (each mode's form unmounts, so entries are cleared).
      maskClosable={false}
      onClose={onBack}
      loading={approve.isPending}
      fetchInstallers={listReplacementFieldInstallers}
      queryKeyPrefix={['replacements', 'installers']}
      onSubmit={(assignment) =>
        approve.mutate(
          { withInstallation: true, ...assignment },
          {
            onSuccess: () => {
              toast.success({ title: REPLACEMENT_APPROVED_MESSAGE });
              onApproved();
            },
            onError: (e) =>
              toast.error({
                title: e instanceof ApiError ? e.message : 'Could not approve. Please try again.',
              }),
          },
        )
      }
    />
  );
}

/** Maps the open overlay to its modal. `onClose` clears the overlay; `onNavigate` moves between
 *  steps (choice → assign, choice → reject); `onActioned` also closes the detail drawer after a
 *  successful approve/reject (the mutation already invalidated the list). */
export function ReplacementActionModals({
  overlay,
  onClose,
  onNavigate,
  onActioned,
}: {
  overlay: ReplacementOverlay;
  onClose: () => void;
  onNavigate: (kind: NonNullable<ReplacementOverlay>['kind']) => void;
  onActioned?: () => void;
}) {
  if (!overlay) return null;
  const { kind, row } = overlay;

  if (kind === 'reject') {
    return (
      <RejectReplacementModal
        open
        id={row.id}
        onClose={onClose}
        onRejected={() => onActioned?.()}
      />
    );
  }

  if (kind === 'approve-assign') {
    return (
      <AssignArm
        id={row.id}
        onBack={() => onNavigate('approve-choice')}
        onApproved={() => onActioned?.()}
      />
    );
  }

  return (
    <ApproveReplacementModal
      open
      id={row.id}
      onClose={onClose}
      onApproved={() => onActioned?.()}
      onWithInstallation={() => onNavigate('approve-assign')}
      onReject={() => onNavigate('reject')}
    />
  );
}
